//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useState, useEffect } from 'react';
import moment from 'moment/moment';
import dayjs from 'dayjs';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';

// === API ===
import { prokeyApi } from 'api/prokey';

// === UTILS ===
import { regex_start_of_word, spaceBeforeWord } from 'components/utils';

import { ReportContext } from '../ReportContext/ReportContext';

//#endregion

//-----------------------------------------------------------------
export function ControlBox( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]
  
  const theme = useTheme();

  const {
    report_name , setReportName ,
    report_data , setReportData ,
    is_loading  , setIsLoading  ,
  } = useContext( ReportContext );

  const [ report_names, setReportNames ] = useState( [] );

  const [ date_start , setDateStart  ] = useState( dayjs() );
  const [ date_end   , setDateEnd    ] = useState( dayjs() );
  const [ success    , setSuccess    ] = useState( false );

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      padding: '0.5rem',
      display: 'flex',
      marginBottom: '0.5rem',
      justifyContent: 'center',
    },
    reports_label: {

    },
    reports_select: {
      marginRight: '0.5rem',
      height: '4rem',
      width: '20rem',
    },
    date_start: {
      marginLeft  : '1rem',
      marginRight : '1rem',
    },
    date_end: {
      marginLeft  : '1rem',
      marginRight : '1rem',
    },
    em_dash: {
      display: 'flex',
      alignItems: 'center',
    },
    button_report: {
      marginLeft : '0.5rem' ,
      height     : '4rem'           ,
      width      : '15rem'          ,
      backgroundColor: success ? theme.palette.success.main : theme.palette.primary.main,
      '&:hover': {
        backgroundColor: success ? theme.palette.success.dark : theme.palette.primary.main.dark,
      },
    },
    button_download: {
      position   : 'absolute',
      right      : '2.5rem',
      // marginLeft : 'auto'  ,
      height     : '4rem'  ,
      width      : '14rem' ,
    }
  }


  // === ATTRIBUTES ===
  const
    CONTAINER = {
      sx: _styles.container,
    },
    REPORTS_LABEL = {
      id: 'reports-label',
      children : [ 'Report Name' ],
    },
    REPORTS_SELECT = {
      id       : 'reports-select',
      labelId  : 'reports-label' ,
      sx       : _styles.reports_select ,
      value    : report_name            ,
      label    : 'Report Name'          ,
      onChange : (event) => setReportName(event.target.value),
      children : report_names.map( (name) => <MenuItem value={name}>{name}</MenuItem> ),
    },
    DATE_START = {
      sx       : _styles.date_start ,
      value    : date_start         ,
      label    : 'Start'            ,
      onChange : setDateStart       ,
    },
    DATE_END = {
      sx       : _styles.date_end ,
      value    : date_end         ,
      label    : 'End'            ,
      onChange : setDateEnd       ,
    },
    EM_DASH = {
      sx      : _styles.em_dash ,
      children: ['—']           ,
    },
    BUTTON_REPORT = {
      sx       : _styles.button_report ,
      variant  : 'contained'           ,
      color    : 'primary'             ,
      disabled : is_loading            ,
      endIcon  : success ? <DoneIcon /> : <SendIcon />,
      onClick  : handleRunReport ,
      children : ['RUN REPORT']  ,
    },
    BUTTON_DOWNLOAD = {
      sx       : _styles.button_download ,
      variant  : 'contained'             ,
      disabled : is_loading || !success  ,
      startIcon: <SaveIcon />            ,
      onClick  : downloadCsv             ,
      children : ['Download (.csv)']     ,
    };

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    async function getReportNames() {
      //-----------------------------------------------------------------
      try {
        const response = await prokeyApi.getReportNames();
        if( !response.data ) return;
        const pkStatsNames = response.data.filter( name => name.startsWith('pk_stats_'))
        const names = pkStatsNames.map( x => {
          return x
            .replace('pk_stats_', '')
           // .replace(regex_start_of_word, spaceBeforeWord);
        } );
        setReportNames( names );
      } catch(error) {
        console.warn(error);
      }
    }

    getReportNames();
  }, []);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  async function handleRunReport() {
    //-----------------------------------------------------------------

    const has_valid_params = report_name && date_start && date_end;

    if ( !has_valid_params ) {
      // setErrMessage('Report name & date range must be provided');
      // setErrOpen(true);
      setIsLoading(false);

      return;
    }

    setIsLoading( true  );
    setSuccess( false );
    setReportData([]);

    try {
      let storedProc = 'pk_stats_' + report_name.replace(/ /g, '');  
      let iso_date_start = moment( new Date( date_start ).setHours(0, 0, 0, 0)).toISOString();
      let iso_date_end   = moment( new Date( date_end   ).setHours(23, 59, 59)).toISOString();

      const response = await prokeyApi.getReport( storedProc, iso_date_start, iso_date_end );
      setIsLoading(false);

      if( !response.data ) {
        // setErrMessage('No data available to download');
        // setErrOpen(true);
        return;
      }
  
      setReportData( response.data );
      setSuccess(true);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      // setErrMessage('Unable to run query at this time');
      // setErrOpen(true);
    }
  };

  //-----------------------------------------------------------------
  function downloadCsv() {
    //-----------------------------------------------------------------
    if( !report_data || report_data.length <= 0 ) {      
      // setErrMessage( 'No data available to download' );
      // setErrOpen( true );
      return;
    }

    // {ReportName}_{start}-to-{end}.csv
    const file_name = `${report_name.replaceAll(' ', '')}_${getYYYYMMDD( new Date( date_start ) )}-to-${getYYYYMMDD( new Date( date_end ) )}`;

    const csv_string    = createCsvString();
    const csv_blob      = new Blob( [csv_string], { type: 'text/csv' } );
    const csv_url       = window.URL.createObjectURL( csv_blob );
    const csv_hyperlink = document.createElement('a');

    csv_hyperlink.setAttribute('href', csv_url);
    csv_hyperlink.setAttribute('download', file_name);
    csv_hyperlink.click();
  };

  //-----------------------------------------------------------------
  function getYYYYMMDD( date ) {
    //-----------------------------------------------------------------
    const result = date.getFullYear() + ( '0' + (date.getMonth() + 1) ).slice(-2) + ( '0' + date.getDate() ).slice(-2);
    return result;
  }

  //-----------------------------------------------------------------
  function createCsvString() {
    //-----------------------------------------------------------------
    try {
      const headers = Object.keys(report_data[0]);

      let result = headers.join(',') + '\n';
      for (let row of report_data) {
        result += Object.values(row).join(',') + '\n';
      }

      return result;
    } catch (error) {
      console.log(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box { ...CONTAINER }>
        <FormControl>
          <InputLabel { ...REPORTS_LABEL  } />
          <Select     { ...REPORTS_SELECT } />
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker { ...DATE_START } />
          <Box        { ...EM_DASH    } />
          <DatePicker { ...DATE_END   } />
        </LocalizationProvider>
        <Button { ...BUTTON_REPORT } />
        {/* <Box sx={{display: 'flex', justifyContent: 'center'}}>
        </Box> */}
        <Button { ...BUTTON_DOWNLOAD } />
      </Box>
    );
  };

  //#endregion

  return display();
}